import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  ResourceRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatList, MatListItem, MatListItemIcon, MatListItemLine, MatListItemMeta, MatListItemTitle } from '@angular/material/list';
import { AlertComponent, ButtonComponent, IconButtonComponent, SpinnerComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import type { OrderHistoryRecordModel, ReceiptDocumentModel } from '@libs/services/models';
import type { IconName } from '@libs/shared/models';
import { DownloadTrackingService } from '@libs/shared/tracking';
import { LuxonDatePipe } from '@libs/shared/ui';
import { OrderHistoryService } from '../services';

export interface OrderHistoryDocumentTileModel {
  document: ReceiptDocumentModel;
  icon: IconName;
  meta: string;
}

// Icon per mime type; other types get the generic file icon
const DOCUMENT_ICONS: Record<string, IconName> = {
  'application/pdf': 'file-pdf',
  'application/xml': 'file-xml',
};

@Component({
  selector: 'ui-order-history-documents',
  templateUrl: './order-history-documents.component.html',
  styleUrls: ['./order-history-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemMeta,
    MatListItemTitle,
    SpinnerComponent,
  ],
})
export class OrderHistoryDocumentsComponent {
  public order: InputSignal<OrderHistoryRecordModel> = input.required();
  public closed: OutputEmitterRef<void> = output<void>();

  public documentsResource: ResourceRef<ReceiptDocumentModel[] | undefined> = rxResource({
    params: () => this.order().id,
    stream: ({ params: orderId }) => this.orderHistoryService.getOrderDocuments(orderId),
  });

  // value() throws while the resource is in the error state, so it must stay behind the hasValue() guard
  public documents: Signal<ReceiptDocumentModel[]> = computed(() =>
    this.documentsResource.hasValue() ? this.documentsResource.value() : [],
  );

  public documentTiles: Signal<OrderHistoryDocumentTileModel[]> = computed(() =>
    this.documents().map((document: ReceiptDocumentModel) => ({
      document,
      icon: DOCUMENT_ICONS[document.mimeType] ?? 'file-lines',
      meta: this.documentMetaData(document),
    })),
  );

  public countLabel: Signal<string> = computed(() => {
    const count: number = this.documents().length;

    return count === 1 ? '1 Dokument verfügbar' : `${count} Dokumente verfügbar`;
  });

  // Every in-flight download keeps its own loading indicator, so parallel downloads don't clear each other
  public downloadingFileNames: WritableSignal<ReadonlySet<string>> = signal(new Set());
  public downloadFailed: WritableSignal<boolean> = signal(false);

  private readonly orderHistoryService: OrderHistoryService = inject(OrderHistoryService);
  private readonly downloadTracking: DownloadTrackingService = inject(DownloadTrackingService);
  private readonly luxonDatePipe: LuxonDatePipe = new LuxonDatePipe();

  public onDownload(receiptDocument: ReceiptDocumentModel): void {
    this.downloadTracking.trackDownload(receiptDocument.fileName);
    this.downloadFailed.set(false);
    this.toggleDownloading(receiptDocument.fileName, true);

    void this.orderHistoryService.downloadOrderDocument(receiptDocument)
      .catch(() => void this.downloadFailed.set(true))
      .finally(() => void this.toggleDownloading(receiptDocument.fileName, false));
  }

  public onClose(): void {
    this.closed.emit();
  }

  private toggleDownloading(fileName: string, downloading: boolean): void {
    this.downloadingFileNames.update((fileNames: ReadonlySet<string>) => {
      const next: Set<string> = new Set(fileNames);

      if (downloading) {
        next.add(fileName);
      } else {
        next.delete(fileName);
      }

      return next;
    });
  }

  private documentMetaData(document: ReceiptDocumentModel): string {
    // 'application/pdf' → 'PDF'
    const type: string = document.mimeType.slice(document.mimeType.indexOf('/') + 1).toUpperCase();

    return [
      document.description,
      this.luxonDatePipe.transform(document.date),
      type,
    ].filter(Boolean).join(' · ');
  }
}
